import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Globe, LogOut, ArrowLeft, User, Shield, Bell, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [passwordData, setPasswordData] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!passwordData.current || !passwordData.new || !passwordData.confirm) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (passwordData.new !== passwordData.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new.length < 6) {
      toast.error('New password must be at least 6 characters long');
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      await axios.post(`${API}/auth/change-password`, {
        user_id: user.id,
        current_password: passwordData.current,
        new_password: passwordData.new
      });
      
      toast.success('Password updated successfully!');
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Current password is incorrect');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to change password');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className='min-h-screen pb-20 md:pb-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      <nav className='bg-white border-b shadow-sm'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center h-16'>
            <div className='flex items-center space-x-4'>
              <Button variant='ghost' size='icon' onClick={() => navigate('/dashboard')} data-testid='back-to-dashboard-btn'>
                <ArrowLeft className='w-5 h-5' />
              </Button>
              <div className='flex items-center space-x-3'>
                <img 
                  src='https://customer-assets.emergentagent.com/job_smart-money-route/artifacts/6teoyzj7_Generated%20Image%20November%2018%2C%202025%20-%203_06PM.png' 
                  alt='RouteFlow AI' 
                  className='h-10 w-auto'
                />
                <span className='text-xl font-bold gradient-text'>RouteFlow AI</span>
              </div>
            </div>
            <Button variant='ghost' onClick={onLogout} data-testid='logout-btn'>
              <LogOut className='w-4 h-4 mr-2' /> Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className='max-w-4xl mx-auto px-4 py-8'>
        <div className='mb-6'>
          <h1 className='text-3xl font-bold text-gray-900 mb-2'>Account Settings</h1>
          <p className='text-gray-600'>Manage your profile and preferences</p>
        </div>

        <Tabs defaultValue='profile' className='w-full'>
          <TabsList className='grid w-full grid-cols-4 mb-6'>
            <TabsTrigger value='profile' data-testid='profile-tab'>
              <User className='w-4 h-4 mr-2' /> Profile
            </TabsTrigger>
            <TabsTrigger value='security' data-testid='security-tab'>
              <Shield className='w-4 h-4 mr-2' /> Security
            </TabsTrigger>
            <TabsTrigger value='wallet' data-testid='wallet-tab'>
              <Wallet className='w-4 h-4 mr-2' /> Wallet
            </TabsTrigger>
            <TabsTrigger value='notifications' data-testid='notifications-tab'>
              <Bell className='w-4 h-4 mr-2' /> Notifications
            </TabsTrigger>
          </TabsList>

          <TabsContent value='profile'>
            <Card className='border-0 shadow-lg' data-testid='profile-card'>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Your account details</CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='space-y-2'>
                  <Label>Full Name</Label>
                  <Input value={user.full_name} disabled />
                </div>
                <div className='space-y-2'>
                  <Label>Email Address</Label>
                  <Input value={user.email} disabled />
                </div>
                <div className='space-y-2'>
                  <Label>Member Since</Label>
                  <Input value={new Date(user.created_at).toLocaleDateString()} disabled />
                </div>
                <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
                  <p className='text-sm text-blue-800'>
                    <strong>Note:</strong> To update your name or email, please contact support at support@routeflowai.com
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value='security'>
            <Card className='border-0 shadow-lg' data-testid='security-card'>
              <CardHeader>
                <CardTitle>Security Settings</CardTitle>
                <CardDescription>Keep your account secure</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className='space-y-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='current-password'>Current Password</Label>
                    <Input
                      id='current-password'
                      type='password'
                      value={passwordData.current}
                      onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                      data-testid='current-password-input'
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='new-password'>New Password</Label>
                    <Input
                      id='new-password'
                      type='password'
                      value={passwordData.new}
                      onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                      data-testid='new-password-input'
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='confirm-password'>Confirm New Password</Label>
                    <Input
                      id='confirm-password'
                      type='password'
                      value={passwordData.confirm}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                      data-testid='confirm-password-input'
                    />
                  </div>
                  <Button 
                    type='submit' 
                    className='bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700' 
                    data-testid='change-password-btn'
                    disabled={isChangingPassword}
                  >
                    {isChangingPassword ? 'Changing...' : 'Change Password'}
                  </Button>
                </form>

                <div className='mt-8 pt-8 border-t'>
                  <h3 className='font-semibold mb-4'>Two-Factor Authentication</h3>
                  <div className='bg-amber-50 border border-amber-200 rounded-lg p-4'>
                    <p className='text-sm text-amber-800 mb-3'>
                      <strong>Coming Soon:</strong> Two-factor authentication will be available in the next update for enhanced account security.
                    </p>
                    <Button variant='outline' disabled>
                      Enable 2FA
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value='wallet'>
            <Card className='border-0 shadow-lg' data-testid='wallet-card'>
              <CardHeader>
                <CardTitle>Wallet Information</CardTitle>
                <CardDescription>Your non-custodial wallet details</CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='space-y-2'>
                  <Label>Wallet Address</Label>
                  <div className='flex space-x-2'>
                    <Input value={user.wallet_address} disabled className='font-mono text-sm' />
                    <Button 
                      variant='outline'
                      onClick={() => {
                        navigator.clipboard.writeText(user.wallet_address);
                        toast.success('Address copied!');
                      }}
                      data-testid='copy-wallet-btn'
                    >
                      Copy
                    </Button>
                  </div>
                </div>
                
                <div className='bg-emerald-50 border border-emerald-200 rounded-lg p-4 mt-6'>
                  <h4 className='font-semibold text-emerald-900 mb-2'>Non-Custodial Security</h4>
                  <ul className='text-sm text-emerald-800 space-y-1'>
                    <li>✓ You control your private keys</li>
                    <li>✓ We never access your funds</li>
                    <li>✓ Connect external wallets anytime</li>
                    <li>✓ Full ownership of your assets</li>
                  </ul>
                </div>

                <div className='mt-6'>
                  <h4 className='font-semibold mb-3'>Connected Wallets</h4>
                  <Button 
                    onClick={() => navigate('/crypto-payment')}
                    className='bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700'
                    data-testid='connect-external-wallet-btn'
                  >
                    Connect MetaMask Wallet
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value='notifications'>
            <Card className='border-0 shadow-lg' data-testid='notifications-card'>
              <CardHeader>
                <CardTitle>Notification Preferences</CardTitle>
                <CardDescription>Choose how you want to be notified</CardDescription>
              </CardHeader>
              <CardContent className='space-y-6'>
                <div className='space-y-4'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <p className='font-semibold'>Transfer Notifications</p>
                      <p className='text-sm text-gray-600'>Updates on your transfers</p>
                    </div>
                    <Button variant='outline' size='sm' data-testid='transfer-notif-toggle'>
                      Enabled
                    </Button>
                  </div>

                  <div className='flex items-center justify-between'>
                    <div>
                      <p className='font-semibold'>Transaction Completed</p>
                      <p className='text-sm text-gray-600'>When money is delivered</p>
                    </div>
                    <Button variant='outline' size='sm' data-testid='completed-notif-toggle'>
                      Enabled
                    </Button>
                  </div>

                  <div className='flex items-center justify-between'>
                    <div>
                      <p className='font-semibold'>Marketing Updates</p>
                      <p className='text-sm text-gray-600'>News and promotions</p>
                    </div>
                    <Button variant='outline' size='sm' data-testid='marketing-notif-toggle'>
                      Disabled
                    </Button>
                  </div>

                  <div className='flex items-center justify-between'>
                    <div>
                      <p className='font-semibold'>Security Alerts</p>
                      <p className='text-sm text-gray-600'>Login and security events</p>
                    </div>
                    <Button variant='outline' size='sm' data-testid='security-notif-toggle'>
                      Enabled
                    </Button>
                  </div>
                </div>

                <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6'>
                  <p className='text-sm text-blue-800'>
                    <strong>Email notifications:</strong> All critical notifications will be sent to {user.email}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProfilePage;
